import Compressor from 'compressorjs';
import imageCompression from 'browser-image-compression';
import { IMB_ImageCompressorProps } from './MB_ImageCompressor';
import { MB_imageCompressorUtils } from './MB_ImageCompressorUtils';

/**
 * A bit slow at compressing files but produces great results for any non-jpeg images
 * @param file - Blob image that we want to compress
 * @param onProgress - Callback function to receive compression completion percentage
 * @param maxSizeAfterCompression - If provided, tries to compress the image to have smaller or equal size to this number
 * @returns - Promise containing the compressed image
 */
const browserImageCompression = ({
  blob,
  onProgress,
  maxSizeAfterCompression,
}: {
  blob: Blob;
  onProgress: (progress: number | undefined) => void;
  maxSizeAfterCompression?: number;
}): Promise<{ blob: Blob; uri: string }> => {
  onProgress(0);
  return new Promise<{ blob: Blob; uri: string }>((resolve, reject) => {
    //Note: Had to cast the blob to any otherwise typescript was complaining.
    imageCompression(blob as any, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      onProgress: onProgress,
    })
      .then((compressedFile: Blob) => {
        if (maxSizeAfterCompression !== undefined && compressedFile.size > maxSizeAfterCompression) {
          reject(MB_imageCompressorUtils.fileIsTooLargeString(maxSizeAfterCompression));
        } else {
          resolve({ blob: compressedFile, uri: URL.createObjectURL(compressedFile) });
        }
      })
      .catch(error => {
        reject(error);
      })
      .finally(() => onProgress(undefined));
  });
};

/**
 * Fast and good compression for jpeg images. Struggles with compressing images that have transparencies.
 * Falls back to browserImageCompression if unable to compress file to less than max allowed file size.
 * @param file - Blob image that we want to compress
 * @param onProgress - Callback function to receive compression completion percentage
 * @param maxSizeAfterCompression - If provided, tries to compress the image to have smaller or equal size to this number
 * @returns - Promise containing the compressed image
 */
const compressorjsCompression = async ({
  blob,
  onProgress,
  maxSizeAfterCompression,
}: {
  blob: Blob;
  onProgress: (progress: number | undefined) => void;
  maxSizeAfterCompression?: number;
}): Promise<{ blob: Blob; uri: string }> => {
  return new Promise<{ blob: Blob; uri: string }>((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const compressor = new Compressor(blob, {
      quality: 0.6,
      maxWidth: 1920,
      success: result => {
        if (maxSizeAfterCompression !== undefined && result.size > maxSizeAfterCompression) {
          browserImageCompression({ blob, onProgress, maxSizeAfterCompression })
            .then(compressedImage => resolve(compressedImage))
            .catch(() => reject(MB_imageCompressorUtils.fileIsTooLargeString(maxSizeAfterCompression)));
        } else {
          resolve({ blob: result, uri: URL.createObjectURL(result) });
        }
      },
      error(error) {
        reject(error);
      },
    });
  });
};

/**
 * Image compressor. Uses either browser-image-compression or compressorjs to compress the image.
 * The reason why we use two separate libraries is because compressorjs is much faster but it was not doing great
 * when compressing any image besides jpeg images.
 *
 * @param {Blob | string | undefined} props.blobOrUrl blob or a local file uri for the image.
 * @param {function} props.onProgress Callback function for the compression progress.
 * @param {number} props.maxSizeAfterCompression If provided, throws an error if compressed image has bigger size than this
 * @param {boolean} props.withBrowserImageCompression If passed, always uses browser-image-compression library to handle the compression.
 * @returns If successful, returns the following.
 * For web, it returns blob and uri for the compressed image.
 * For mobile, it returns uri for the compressed image.
 */
const MB_imageCompressor = async ({
  blobOrUrl,
  onProgress,
  maxSizeAfterCompression,
  withBrowserImageCompression,
}: IMB_ImageCompressorProps): Promise<{ blob: Blob | undefined; uri: string }> => {
  if (!blobOrUrl) {
    throw 'MB_ImageCompressor: Error: Blob or url was empty or undefined';
  }

  let file = blobOrUrl;
  if (typeof blobOrUrl === 'string') {
    const response = await fetch(blobOrUrl);
    file = await response.blob();
  }

  if (withBrowserImageCompression) {
    return browserImageCompression({ blob: file as Blob, onProgress, maxSizeAfterCompression });
  } else {
    return compressorjsCompression({ blob: file as Blob, onProgress, maxSizeAfterCompression });
  }
};

export { MB_imageCompressor };
